import t from "@/lang/lang";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import { FieldErrors } from "@/shared/components/field/field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { TextInputField } from "@/shared/components/field/input-field";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { DocumentBaseDialog } from "@/services/backend/documents/components/document-base-dialog";
import { CustomerAddressCard } from "@/services/backend/addresses/components/customer-address-card";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { ServiceObjectReferencesBaseCard } from "@/services/backend/documents/components/service-object-references-base-card";
import { ServiceObjectsSelect } from "@/services/backend/customers/components/service-objects-select";
import {
  ServiceObjectReference,
  updateServiceObjectsByIds,
} from "@/services/backend/documents/types/service-object-references-base";
import { useResetRemovedServiceObjectFields } from "@/services/backend/documents/lib/use-reset-removed-service-object-fields";
import { PaymentTermsCard } from "@/services/backend/billing/components/payment-terms-card";
import { BillingInformationCard } from "@/services/backend/billing/components/billing-information-card";
import { OfferReferenceCard } from "@/services/backend/documents/components/offer-reference-card";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { contractTextVariables } from "../types/contract-text-variables";
import {
  PatchRequest,
  updateOmniContractCache,
  useOmniContractPatchMutation,
  useOmniContractResetReferenceNumberMutation,
} from "../api/contract-api";
import { Contract, Patch, patchContract } from "../types/contract";
import { ContractStateField } from "./contract-state-field";

export function ContractDataPage({ contract }: { contract: Contract }) {
  const request: PatchRequest = { id: contract.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateOmniContractCache,
    useOmniContractPatchMutation(),
  );
  const patch = (p: Patch) =>
    set({ ...request, ...p }, patchContract(contract, p));
  const disabled = isImmutable(contract);

  return (
    <div className="grid gap-2">
      <ContractCard contract={contract} onPatch={patch} disabled={disabled} />
      <div className="grid gap-2 xl:grid-cols-2">
        <OfferReferenceCard
          reference={contract}
          onPatch={patch}
          destinationId={contract.id}
          destinationEntityName={CpyName.OmniContract}
          sourceEntityName={CpyName.OmniOffer}
          sourcesParams={{
            serviceKind: { active: true, values: [contract.serviceKind] },
            process: {
              active: !!contract.processId,
              values: [contract.processId ?? NullUUID],
            },
            orderBy: [{ field: "updatedAt" }],
          }}
          description={t("Das Angebot, auf das sich der Vertrag bezieht.")}
          disabled={disabled}
        />
        <CustomerAddressCard
          customerAddress={contract.customerAddress}
          onCustomerAddressChange={(customerAddress) =>
            patch({ customerAddress })
          }
          description={t(
            "Adresse, an welche der Vertrag geschickt werden soll.",
          )}
          fieldName="customerAddress"
          disabled={disabled}
        />
        <PaymentTermsCard
          paymentTerms={contract.paymentTerms}
          onPaymentTermsChange={(paymentTerms) => patch({ paymentTerms })}
          disabled={disabled}
        />
        <BillingInformationCard
          billingInformation={contract.billingInformation}
          onBillingInformationChange={(billingInformation) =>
            patch({ billingInformation })
          }
          disabled={disabled}
        />
        <ServiceObjectReferencesBaseCard
          references={contract}
          onReferencesChange={(refs) => patch(refs)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function ContractCard({
  contract,
  onPatch,
  disabled,
}: {
  contract: Contract;
  onPatch: (patch: Patch) => void;
  disabled: boolean;
}) {
  const patchReferenceNumber = useOmniContractPatchMutation();
  const resetReferenceNumber = useOmniContractResetReferenceNumberMutation();
  const { errors: customerErrors } = useFieldErrors("customerId");
  const resetRemovedServiceObjectFields = useResetRemovedServiceObjectFields();

  const variables = contractTextVariables(contract);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Vertrag")}</CardTitle>
        <CardDescription>
          {t(
            "Kerndaten des Vertrags. Erweiterte Informationen können über das Info-Icon aufgerufen werden.",
          )}
        </CardDescription>
        <CardAction>
          <DocumentBaseDialog
            document={contract}
            onPatch={onPatch}
            disabled={disabled}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-4">
        <ReferenceNumberField
          data={contract}
          updateCache={updateOmniContractCache}
          patchMutation={patchReferenceNumber}
          resetMutation={resetReferenceNumber}
          disabled={disabled}
        />
        <ContractStateField
          value={contract.contractState}
          onValueChange={(contractState) => onPatch({ contractState })}
          disabled={disabled}
        />
        <DatePickerField
          label={t("Vertragsbeginn")}
          nullable={true}
          date={contract.startDate}
          onDateChange={(date) => onPatch({ startDate: date ?? null })}
          fieldName="startDate"
          captionLayout="dropdown"
          disabled={disabled}
        />
        <DatePickerField
          label={t("Vertragsende")}
          nullable={true}
          date={contract.endDate}
          onDateChange={(date) => onPatch({ endDate: date ?? null })}
          fieldName="endDate"
          description={t("Ohne Vertragsende ist der Vertrag unbefristet.")}
          captionLayout="dropdown"
          disabled={disabled}
        />
        <Field className="lg:col-span-2">
          <FieldLabel>{t("Kunde")}</FieldLabel>
          <CustomerSelect value={contract.customerId} disabled />
          <FieldErrors errors={customerErrors} />
        </Field>
        <Field className="lg:col-span-2">
          <FieldLabel>{t("Serviceobjekt")}</FieldLabel>
          <ServiceObjectsSelect
            customerId={contract.customerId}
            value={contract.serviceObjects.map(
              (s: ServiceObjectReference) => s.serviceObjectId,
            )}
            onValueChange={(ids) => {
              resetRemovedServiceObjectFields(contract, ids);
              onPatch(updateServiceObjectsByIds(contract, ids));
            }}
            disabled={disabled}
          />
        </Field>
        <TextInputField
          label={t("Titel")}
          text={contract.title}
          onTextChange={(title) => onPatch({ title })}
          fieldName="title"
          fieldProps={{ className: "lg:col-span-2" }}
          disabled={disabled}
        />
        <TextInputField
          label={t("Untertitel")}
          text={contract.subtitle}
          onTextChange={(subtitle) => onPatch({ subtitle })}
          fieldName="subtitle"
          fieldProps={{ className: "lg:col-span-2" }}
          disabled={disabled}
        />
        <EditorInputField
          label={t("Besondere Vereinbarungen")}
          initialHtml={contract.specialAgreements}
          onHtmlChange={(specialAgreements) => onPatch({ specialAgreements })}
          fieldName="specialAgreements"
          fieldProps={{ className: "lg:col-span-4" }}
          disabled={disabled}
        />
        <EditorInputField
          // The editor highlights the variables it is mounted with.
          key={variables.map((v) => v.name).join(",")}
          label={t("Vertragstext")}
          initialHtml={contract.text}
          onHtmlChange={(text) => onPatch({ text })}
          variables={variables}
          fieldName="text"
          fieldProps={{ className: "lg:col-span-4" }}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}
